import type { ResponsiveLayout, LayoutValues } from '~/types/strapi';

/**
 * Generate CSS properties for layout values
 */
export function generateLayoutCSS(values: LayoutValues): string | null {
  const properties: string[] = [];
  
  if (values.arrangement) {
    // Normalize arrangement value to handle underscore format from data
    const normalizedArrangement = values.arrangement.replace('_', '-');
    
    switch (normalizedArrangement) {
      case 'row':
        properties.push('display: grid; grid-template-columns: 1fr 1fr; gap: inherit; align-items: start');
        break;
      case 'row-inverted':
        properties.push('display: grid; grid-template-columns: 1fr 1fr; gap: inherit; align-items: start; direction: rtl');
        break;
      case 'column':
        properties.push('display: flex; flex-direction: column');
        break;
      case 'column-inverted':
        properties.push('display: flex; flex-direction: column-reverse');
        break;
      case 'three-cell':
        properties.push('display: grid; grid-template-columns: 1fr 1fr 1fr');
        break;
      case 'three-cell-inverted':
        properties.push('display: grid; grid-template-columns: 1fr 1fr 1fr; direction: rtl');
        break;
    }
  }
  
  if (values.gap !== undefined) {
    properties.push(`gap: ${values.gap}px`);
  }
  
  // Handle alignment for flex layouts
  if (values.alignment && (values.arrangement?.includes('row') || values.arrangement?.includes('column'))) {
    const normalizedArrangement = values.arrangement.replace('_', '-');
    
    if (normalizedArrangement.includes('row')) {
      // For row layouts: vertical alignment using align-items
      switch (values.alignment) {
        case 'start':
          properties.push('align-items: flex-start');
          break;
        case 'center':
          properties.push('align-items: center');
          break;
        case 'end':
          properties.push('align-items: flex-end');
          break;
      }
    } else if (normalizedArrangement.includes('column')) {
      // For column layouts: horizontal alignment using justify-content
      switch (values.alignment) {
        case 'start':
          properties.push('justify-content: flex-start');
          break;
        case 'center':
          properties.push('justify-content: center');
          break;
        case 'end':
          properties.push('justify-content: flex-end');
          break;
      }
    }
  }
  
  return properties.length > 0 ? properties.join('; ') : null;
}

/**
 * Generate custom CSS for responsive layout values
 */
export function generateCustomLayoutCSS(layout: ResponsiveLayout, className: string): string | null {
  const rules: string[] = [];
  
  // Mobile (default) - use general arrangement or mobile specific
  const mobileLayout = layout.mobile || (layout.generalArrengement ? { 
    arrangement: layout.generalArrengement, 
    gap: layout.gap,
    alignment: layout.alignment 
  } : null);
  if (mobileLayout) {
    const mobileCSS = generateLayoutCSS(mobileLayout);
    if (mobileCSS) {
      rules.push(`.${className}--layout-custom { ${mobileCSS} }`);
    }
  }
  
  // Tablet
  if (layout.tablet) {
    const tabletCSS = generateLayoutCSS(layout.tablet);
    if (tabletCSS) {
      rules.push(`@media (min-width: 768px) { .${className}--layout-custom { ${tabletCSS} } }`);
    }
  }
  
  // Desktop
  if (layout.desktop) {
    const desktopCSS = generateLayoutCSS(layout.desktop);
    if (desktopCSS) {
      rules.push(`@media (min-width: 1024px) { .${className}--layout-custom { ${desktopCSS} } }`);
    }
  }
  
  // Wide
  if (layout.wide) {
    const wideCSS = generateLayoutCSS(layout.wide);
    if (wideCSS) {
      rules.push(`@media (min-width: 1280px) { .${className}--layout-custom { ${wideCSS} } }`);
    }
  }
  
  // XLarge
  if (layout.xlarge) {
    const xlargeCSS = generateLayoutCSS(layout.xlarge);
    if (xlargeCSS) {
      rules.push(`@media (min-width: 1536px) { .${className}--layout-custom { ${xlargeCSS} } }`);
    }
  }
  
  return rules.length > 0 ? rules.join('\n') : null;
}

/**
 * Generate CSS classes for layout based on layout configuration
 */
export function generateLayoutClasses(layout?: ResponsiveLayout, className?: string): string[] {
  if (!layout) return [];
  
  const classes: string[] = [];
  
  // If we have any layout configuration, use custom layout
  if (layout.generalArrengement || layout.mobile || layout.tablet || layout.desktop || layout.wide || layout.xlarge) {
    const customCSS = generateCustomLayoutCSS(layout, className || 'content-section');
    if (customCSS) {
      // Note: CSS injection should be handled by the calling component
      return [`${className || 'content-section'}--layout-custom`];
    }
  }
  
  return classes;
} 